/**
 * Shared API + domain types for the PakGames admin CMS.
 * Mirrors api/prisma/schema.prisma and the common envelope in
 * api/src/common/envelope.ts. Money fields are `string | number` (Decimal on
 * the wire) — always render through lib/utils/money.ts.
 */

// ---- Envelope -------------------------------------------------------------

export interface ApiEnvelope<T> {
  code: number;
  msg: string;
  msgCode?: number;
  data: T;
  serviceNowTime?: number;
}

export const ApiCodes = {
  SUCCESS: 0,
  ERROR: 1,
  TOKEN_EXPIRED: 4,
  UNAUTHORIZED: 4,
  MAINTENANCE: 14,
  NETWORK: 13,
  NOT_IMPLEMENTED: 501,
} as const;

/** users/bets/issues/audit-logs return this. */
export interface Paginated<T> {
  list: T[];
  total: number;
  pageNo: number;
  pageSize: number;
}

/** withdrawals/recharges/banners/settings/customer-service/notices return a bare array. */
export type BareList<T> = T[];

export type Money = string | number;

// ---- Enums ----------------------------------------------------------------

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'SUPPORT' | 'FINANCE';
export type UserStatus = 'ACTIVE' | 'BANNED' | 'DELETED';
export type WalletType = 'MAIN' | 'SAFE' | 'BONUS';
export type LedgerType =
  | 'RECHARGE' | 'WITHDRAW' | 'BET' | 'WIN' | 'REFUND' | 'BONUS'
  | 'COMMISSION' | 'ADJUST' | 'SAFE_IN' | 'SAFE_OUT' | 'ACTIVITY' | 'VIP';
export type OrderStatus =
  | 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'CANCELLED' | 'REJECTED';
export type GameCode =
  | 'WINGO' | 'K3' | 'D5' | 'D4' | 'BINGO18' | 'TRX' | 'XOSO' | 'MOTORACE';
export type IssueStatus = 'OPEN' | 'CLOSED' | 'DRAWN' | 'SETTLED';
export type BetStatus = 'PENDING' | 'WON' | 'LOST' | 'CANCELLED' | 'REFUNDED';
export type C2CStatus =
  | 'PENDING' | 'MATCHED' | 'PAID' | 'CONFIRMED' | 'CANCELLED' | 'APPEAL';
export type ActivityType =
  | 'SIGN_IN' | 'DAILY_TASK' | 'FIRST_RECHARGE' | 'TURNTABLE'
  | 'POINT_MALL' | 'CHAMPIONSHIP' | 'GIFT_PACK' | 'OTHER';

// ---- Auth -----------------------------------------------------------------

export interface AdminProfile {
  id: string;
  email: string;
  role: AdminRole;
  name: string | null;
  status?: UserStatus;
  mustChangePassword?: boolean;
  lastLoginAt?: string | null;
}

export interface LoginResponse {
  token: string;
  admin: AdminProfile;
}

export interface AdminJwtClaims {
  sub: string;
  type: 'admin';
  role: AdminRole;
  exp: number;
  iat: number;
}

// ---- Dashboard ------------------------------------------------------------

export interface DashboardCounts {
  users: number;
  bets: number;
  pendingWithdraw: number;
  pendingRecharge: number;
}

// ---- Users ----------------------------------------------------------------

export interface AdminUserRow {
  id: string;
  phone: string | null;
  email: string | null;
  nickname: string | null;
  status: UserStatus;
  vipLevel: number;
  balance: Money;
  createdAt: string;
}

export interface WalletBalance {
  amount: Money;
  balance: Money;
  safeAmount: Money;
  bonusAmount: Money;
}

// ---- Finance --------------------------------------------------------------

export interface UserRef {
  id: string;
  phone: string | null;
  nickname: string | null;
}

export interface RechargeOrder {
  id: string;
  orderNo: string;
  userId: string;
  user?: UserRef;
  amount: Money;
  methodCode: string;
  status: OrderStatus;
  externalRef: string | null;
  providerPayload?: unknown;
  proof: string | null;
  adminRemark: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface WithdrawOrder {
  id: string;
  orderNo: string;
  userId: string;
  user?: UserRef;
  amount: Money;
  fee: Money;
  methodCode: string;
  bankCardId: string | null;
  accountInfo?: unknown;
  status: OrderStatus;
  externalRef: string | null;
  providerPayload?: unknown;
  adminRemark: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface LedgerEntry {
  id: string;
  userId: string;
  walletType: WalletType;
  type: LedgerType;
  amount: Money;
  balanceBefore: Money;
  balanceAfter: Money;
  referenceType: string | null;
  referenceId: string | null;
  remark: string | null;
  createdAt: string;
}

export interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  type: string;
  minAmount: Money;
  maxAmount: Money;
  enabled: boolean;
  sortOrder: number;
  config?: unknown;
}

// ---- Games ----------------------------------------------------------------

export interface GameType {
  id: string;
  code: GameCode;
  name: string;
  category: string;
  intervalSec: number;
  enabled: boolean;
  sortOrder: number;
  config?: unknown;
}

export interface GameIssue {
  id: string;
  gameTypeId: string;
  gameType?: GameType;
  issueNo: string;
  typeId: number;
  status: IssueStatus;
  openAt: string;
  closeAt: string;
  drawAt: string | null;
  result: string | null;
  resultMeta?: unknown;
  createdAt: string;
}

export interface BetOrder {
  id: string;
  orderNo: string;
  userId: string;
  user?: UserRef;
  gameTypeId: string;
  gameType?: GameType;
  issueId: string;
  issue?: GameIssue;
  playCode: string;
  selection: string;
  amount: Money;
  odds: Money;
  winAmount: Money;
  status: BetStatus;
  settledAt: string | null;
  createdAt: string;
}

// ---- Content --------------------------------------------------------------

export interface Banner {
  id: string;
  title: string | null;
  imageUrl: string;
  linkUrl: string | null;
  type: string;
  sortOrder: number;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface HomeSetting {
  id: string;
  key: string;
  value: unknown;
  updatedAt?: string;
  createdAt?: string;
}

export interface CustomerService {
  id: string;
  name: string;
  type: string;
  link: string | null;
  avatar: string | null;
  sortOrder: number;
  enabled: boolean;
}

export interface SiteNotice {
  id: string;
  userId: string | null;
  title: string;
  content: string;
  type: string;
  isRead: boolean;
  createdAt: string;
}

// ---- Audit ----------------------------------------------------------------

export interface AuditLog {
  id: string;
  adminId: string | null;
  action: string;
  target: string | null;
  meta?: unknown;
  createdAt: string;
}

// ---- Phase B: Analytics ---------------------------------------------------

export interface AnalyticsKpis {
  deposits: number;
  withdrawals: number;
  ggr: number;
  ngr: number;
  bonuses: number;
  commissions: number;
  refunds: number;
  betVolume: number;
  payout: number;
  activeBettors: number;
  totalUsers: number;
  newUsers: number;
  pendingWithdraw: number;
  pendingRecharge: number;
  betCount: number;
  from: string;
  to: string;
}

export interface TimeseriesPoint {
  date: string;
  value: number;
}
export interface TimeseriesResponse {
  list: TimeseriesPoint[];
}

export interface GamePerformance {
  gameTypeId: string;
  gameCode: GameCode;
  name: string;
  betCount: number;
  betVolume: number;
  payout: number;
  margin: number;
  holdPct: number;
}
export interface GamePerformanceResponse {
  from: string;
  to: string;
  list: GamePerformance[];
}

// ---- Phase B: Wallets / Player 360 ---------------------------------------

export interface Wallet {
  id: string;
  userId: string;
  type: WalletType;
  balance: Money;
  currency: string;
}

export interface UserDetail {
  profile: {
    id: string;
    phone: string | null;
    email: string | null;
    nickname: string | null;
    avatar: string | null;
    inviteCode: string;
    invitedById: string | null;
    language: string;
    realName: string | null;
    status: UserStatus;
    vipLevel: number;
    points: number;
    lastLoginAt: string | null;
    createdAt: string;
  };
  wallets: Wallet[];
  totals: {
    totalRecharge: number;
    totalWithdraw: number;
    totalBet: number;
    totalWin: number;
    totalCommission: number;
  };
  counts: { bets: number; invitees: number };
  flags: { has2fa: boolean; kyc: boolean };
  inviter: { id: string; phone: string | null; nickname: string | null; inviteCode: string } | null;
}

export interface BankCard {
  id: string;
  userId: string;
  type: string;
  bankName: string | null;
  accountName: string | null;
  accountNo: string;
  ifsc: string | null;
  upiId: string | null;
  usdtAddress: string | null;
  usdtNetwork: string | null;
  isDefault: boolean;
}

export interface OrderDetail {
  kind: 'recharge' | 'withdraw';
  order: RechargeOrder | WithdrawOrder;
  ledger: LedgerEntry[];
}

// ---- Phase B: Games config ------------------------------------------------

export interface OddsConfig {
  id: string;
  gameTypeId: string;
  playCode: string;
  odds: Money;
  enabled: boolean;
  meta?: unknown;
}

export interface IssueExposure {
  issueId: string;
  list: { playCode: string; selection: string; stake: Money; potentialPayout: Money; betCount: number }[];
  totals: { totalStake: Money; maxLiability: Money; betCount: number };
}

export interface GamePnl {
  gameCode: GameCode;
  betVolume: Money;
  payout: Money;
  ggr: Money;
  holdPct: number;
  betCount: number;
}

// ---- Phase B: Engagement --------------------------------------------------

export interface VipLevel {
  id: string;
  level: number;
  name: string;
  minRecharge: Money;
  minBet: Money;
  bonus: Money;
  benefits?: unknown;
}

export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string | null;
  config?: unknown;
  enabled: boolean;
  startAt: string | null;
  endAt: string | null;
  sortOrder: number;
}

export interface PointMallProduct {
  id: string;
  name: string;
  pointsCost: number;
  stock: number;
  imageUrl: string | null;
  enabled: boolean;
  sortOrder: number;
}

export interface PointMallOrder {
  id: string;
  orderNo: string;
  userId: string;
  user?: UserRef;
  productId: string;
  product?: PointMallProduct;
  pointsCost: number;
  addressId: string | null;
  status: string;
  createdAt: string;
}

// ---- Phase B: Affiliate ---------------------------------------------------

export interface AffiliateOverview {
  totalCommission: number;
  periodCommission: number;
  byLevel: { level: number; amount: number; count: number }[];
  agentCount: number;
  today: number;
  week: number;
  month: number;
  topAgents: { earnerId: string; phone: string | null; nickname: string | null; amount: number }[];
}

export interface AffiliateTreeNode {
  id: string;
  phone: string | null;
  nickname: string | null;
  createdAt: string;
  inviteeCount: number;
  totalRecharge: number;
}
export interface AffiliateTree {
  root: { id: string; phone: string | null; nickname: string | null; inviteCode: string; createdAt: string };
  upline: { id: string; phone: string | null; nickname: string | null } | null;
  invitees: AffiliateTreeNode[];
}

export interface CommissionRecord {
  id: string;
  earnerId: string;
  earner?: UserRef;
  sourceUserId: string;
  sourceUser?: UserRef;
  amount: Money;
  rate: Money;
  level: number;
  referenceId: string | null;
  createdAt: string;
}

// ---- Phase B: Admin management / platform --------------------------------

export interface AdminAccount {
  id: string;
  email: string;
  name: string | null;
  role: AdminRole;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface HealthStatus {
  db: string;
  uptimeSec: number;
  now: string;
  counts: { users: number; admins: number; pendingWithdraw: number; pendingRecharge: number };
}

export interface SettingSchemaItem {
  key: string;
  label: string;
  category: string;
  type: 'boolean' | 'string' | 'number' | 'json' | 'list';
  help?: string;
}

// ---- Phase C: Compliance (KYC / RG / Risk / Reconciliation) ---------------

export type KycStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export interface KycSubmission {
  id: string;
  userId: string;
  user?: UserRef;
  fullName: string;
  idType: string;
  idNumber: string;
  documents?: unknown;
  status: KycStatus;
  reviewerId: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  createdAt: string;
}

export type RgLimitType =
  | 'DEPOSIT_DAILY' | 'DEPOSIT_WEEKLY' | 'DEPOSIT_MONTHLY'
  | 'LOSS_DAILY' | 'LOSS_WEEKLY' | 'WAGER_DAILY' | 'SESSION_MINUTES';
export interface RgLimit {
  id: string;
  userId: string;
  user?: UserRef;
  type: RgLimitType;
  value: Money;
  active: boolean;
  setById: string | null;
  note: string | null;
  createdAt: string;
}

export type ExclusionStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';
export interface SelfExclusion {
  id: string;
  userId: string;
  user?: UserRef;
  reason: string | null;
  startAt: string;
  endAt: string | null;
  status: ExclusionStatus;
  setById: string | null;
  createdAt: string;
}

export type RiskSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type RiskStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'DISMISSED';
export interface RiskAlert {
  id: string;
  userId: string | null;
  user?: UserRef;
  type: string;
  severity: RiskSeverity;
  status: RiskStatus;
  title: string;
  detail?: unknown;
  assignedTo: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  resolveNote: string | null;
  createdAt: string;
}

export interface ReconExceptionRow {
  kind: string;
  orderKind: 'recharge' | 'withdraw';
  orderNo: string;
  userId: string;
  user?: UserRef;
  amount: Money;
  status: OrderStatus;
  externalRef?: string | null;
  createdAt: string;
  ageMinutes: number;
  issue: string;
}
export interface ReconSummary {
  stuckProcessing: number;
  successNoLedger: number;
  refundMissing: number;
  totalExceptions: number;
}

// ---- Fundist (SoftGamings) casino aggregator ------------------------------
// Mirrors api/docs/FUNDIST_INTEGRATION.md §7 (`api/admin/fundist/*`).

export type FundistCategory =
  | 'slots' | 'live' | 'crash' | 'fish' | 'table' | 'sports' | 'other';
export type FundistTxType = 'DEBIT' | 'CREDIT' | 'ROLLBACK';
export type FundistTxStatus = 'OK' | 'FAILED' | 'ROLLED_BACK';

export interface FundistProviderRow {
  merchantId: string;
  vendorCode: string;
  name: string;
  category: FundistCategory;
  enabled: boolean;
  sortOrder: number;
  gameCount: number;
}

/** `GET fundist/providers` — merchant rows + aggregator configuration status. */
export interface FundistProvidersResponse {
  /** FUNDIST_ENABLED master switch. */
  enabled: boolean;
  /** True when credentials/secrets are complete (sync + launch available). */
  configured: boolean;
  /** Non-secret configuration issues; empty when configured. */
  issues: string[];
  providers: FundistProviderRow[];
}

export interface FundistGameRow {
  fundistId: string;
  merchantId: string;
  name: string;
  pageCode: string;
  mobilePageCode?: string | null;
  categories: string[];
  imageUrl?: string | null;
  hasDemo: boolean;
  enabled: boolean;
  sortOrder: number;
  syncedAt?: string | null;
}

/** `POST fundist/sync` result — upserted row counts. */
export interface FundistSyncResult {
  merchants: number;
  games: number;
}

/**
 * `GET fundist/transactions` row — OneWallet audit. Amounts are plain numbers,
 * `createdAt` is epoch ms; phone/nickname/vendor resolve to '' when unknown.
 */
export interface FundistTransactionRow {
  id: string;
  actionId: string;
  tid: string | null;
  type: FundistTxType;
  status: FundistTxStatus;
  userId: string;
  phone: string;
  nickname: string;
  amount: number;
  currency: string;
  gameId: string | null;
  vendorCode: string;
  vendorName: string;
  roundId: string | null;
  relatedActionId: string | null;
  balanceAfter: number | null;
  errorCode: number | null;
  errorMessage: string | null;
  createdAt: number;
}

export interface FundistReportTotals {
  betCount: number;
  betAmount: number;
  winCount: number;
  winAmount: number;
  rollbackCount: number;
  rollbackAmount: number;
  ggr: number;
  activePlayers: number;
}

export interface FundistReportProviderRow {
  merchantId: string;
  vendorCode: string;
  vendorName: string;
  betCount: number;
  betAmount: number;
  winCount: number;
  winAmount: number;
  rollbackCount: number;
  rollbackAmount: number;
  ggr: number;
}

/** `GET fundist/report` — per-provider GGR over [from, to). Providers sorted by GGR desc. */
export interface FundistReport {
  from: string;
  to: string;
  totals: FundistReportTotals;
  providers: FundistReportProviderRow[];
}

export type SupportTicketStatus =
  | 'OPEN'
  | 'PENDING'
  | 'WAITING_PLAYER'
  | 'RESOLVED'
  | 'CLOSED';
export type SupportTicketCategory =
  | 'DEPOSIT'
  | 'WITHDRAW'
  | 'ACCOUNT'
  | 'GAME'
  | 'KYC'
  | 'OTHER';
export type SupportTicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface SupportTicketUser {
  id: string;
  phone: string;
  nickname: string;
  status?: string;
}

export interface SupportMessage {
  id: string;
  authorType: 'PLAYER' | 'AGENT' | 'SYSTEM';
  adminId?: string | null;
  body: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  ticketNo: string;
  userId: string | null;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  assignedAdminId: string | null;
  lastMessageAt: string;
  createdAt: string;
  user?: SupportTicketUser | null;
  assignedAdmin?: { id: string; name: string | null; email: string } | null;
  messages?: SupportMessage[];
}

export interface SupportStats {
  open: number;
  pending: number;
  waiting: number;
  resolvedToday: number;
  unassigned: number;
}

export interface SupportCannedReply {
  id: string;
  title: string;
  body: string;
  category: SupportTicketCategory | null;
  enabled: boolean;
}

export interface SupportMemberHit {
  id: string;
  phone: string;
  nickname: string;
  status: string;
  vipLevel: number;
  createdAt: string;
}

export interface SupportMemberSnapshot {
  user: SupportMemberHit & {
    email?: string | null;
    realName?: string | null;
    lastLoginAt?: string | null;
    wallets: Array<{ type: string; balance: string | number }>;
  };
  kyc: { id: string; status: string; createdAt: string } | null;
  recharges: Array<{
    orderNo: string;
    amount: string | number;
    status: string;
    methodCode: string;
    createdAt: string;
  }>;
  withdraws: Array<{
    orderNo: string;
    amount: string | number;
    status: string;
    methodCode: string;
    createdAt: string;
  }>;
  tickets: Array<{
    id: string;
    ticketNo: string;
    subject: string;
    status: SupportTicketStatus;
  }>;
  notes: Array<{
    id: string;
    body: string;
    createdAt: string;
    admin: { name: string | null; email: string };
  }>;
}
